import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useAuth } from '../hooks/useAuth';
import { apiFetch } from '../lib/api';
import type { UserRole } from '../types/roles';
import {
  canEditOrganizationSettings,
  canManageUsers as roleCanManageUsers,
} from '../utils/roleAccess';
import { getOrganizationIdFromStorage, getTenantScope, setOrganizationScope } from '../utils/authHeaders';
import type { SubscriptionTier } from '../types/subscription';
import { normalizeSubscriptionTier } from '../utils/subscriptionTierNormalize';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  settings: {
    ai_providers?: {
      openai_api_key?: string;
      anthropic_api_key?: string;
      azure_openai_endpoint?: string;
      azure_openai_api_key?: string;
    };
    cost_cap?: number;
    notifications?: {
      email_enabled?: boolean;
      slack_webhook?: string;
    };
    theme?: string;
    language?: string;
  };
  created_at?: string;
  tenant_id: string;
  cost_cap: number;
  api_keys: Record<string, unknown>;
  plan?: string;
  status?: string;
  tier?: SubscriptionTier;
  userRole?: UserRole;
  memberCount?: number;
}

export interface UserOrganization {
  organization: Organization;
  role: UserRole;
  is_owner: boolean;
  joined_at: string;
}

export interface OrganizationContextType {
  currentOrganization: Organization | null;
  userOrganizations: UserOrganization[];
  loading: boolean;
  error: string | null;
  switchOrganization: (organizationId: string) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
  createOrganization: (data: CreateOrganizationData) => Promise<Organization>;
  updateOrganization: (organizationId: string, updates: Partial<Organization>) => Promise<void>;
  isOrganizationOwner: boolean;
  isOrganizationAdmin: boolean;
  canManageUsers: boolean;
  canCreateContent: boolean;
}

interface CreateOrganizationData {
  name: string;
  slug: string;
  description?: string;
  settings?: Organization['settings'];
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeOrganization = (
  rawOrganization: unknown,
  role: UserRole,
  fallbackOrgId: string | null | undefined,
): Organization => {
  const raw = isRecord(rawOrganization) ? rawOrganization : {};
  const id =
    typeof raw.id === 'string'
      ? raw.id
      : fallbackOrgId ?? getOrganizationIdFromStorage() ?? '';
  const plan = typeof raw.plan === 'string' ? raw.plan : 'starter';

  return {
    id,
    name: typeof raw.name === 'string' ? raw.name : 'Organization',
    slug: typeof raw.slug === 'string' ? raw.slug : id,
    description: typeof raw.description === 'string' ? raw.description : undefined,
    settings: isRecord(raw.settings)
      ? (raw.settings as Organization['settings'])
      : {},
    created_at: typeof raw.created_at === 'string' ? raw.created_at : undefined,
    tenant_id: id,
    cost_cap: typeof raw.monthly_cost_cap === 'number' ? raw.monthly_cost_cap : 0,
    api_keys: {},
    plan,
    status: typeof raw.status === 'string' ? raw.status : 'unactivated',
    tier: normalizeSubscriptionTier(plan, 'starter'),
    userRole: role,
    memberCount: typeof raw.user_limit === 'number' ? raw.user_limit : undefined,
  };
};

export const OrganizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [userOrganizations, setUserOrganizations] = useState<UserOrganization[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user, isAuthenticated, loading: authLoading } = useAuth();

  const refreshOrganizations = useCallback(async () => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated || !user) {
      setCurrentOrganization(null);
      setUserOrganizations([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch('/api/v1/organizations/me');
      const organization = normalizeOrganization(response, user.role, user.organization_id);
      const userOrganization: UserOrganization = {
        organization,
        role: user.role,
        is_owner: user.role === 'org_admin' || user.role === 'super_admin',
        joined_at: user.created_at ?? new Date().toISOString(),
      };

      setOrganizationScope(organization.id);
      setCurrentOrganization(organization);
      setUserOrganizations([userOrganization]);
    } catch (organizationError) {
      console.error('Failed to load organization context:', organizationError);
      const nextError =
        organizationError instanceof Error
          ? organizationError.message
          : 'Failed to load organization context';
      setError(nextError);
      setCurrentOrganization(null);
      setUserOrganizations([]);
    } finally {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, user]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    void refreshOrganizations();
  }, [authLoading, refreshOrganizations]);

  const switchOrganization = useCallback(
    async (organizationId: string) => {
      if (!organizationId) {
        setError('Organization ID is required');
        return;
      }

      setOrganizationScope(organizationId);
      await refreshOrganizations();

      const resolvedOrgId = getTenantScope().organizationId;
      if (resolvedOrgId !== organizationId) {
        setError('Unable to switch organization context');
      }
    },
    [refreshOrganizations],
  );

  const createOrganization = useCallback(
    async (_data: CreateOrganizationData): Promise<Organization> => {
      if (currentOrganization) {
        return currentOrganization;
      }

      throw new Error('Organization creation is handled by the canonical registration flow.');
    },
    [currentOrganization],
  );

  const updateOrganization = useCallback(
    async (organizationId: string, updates: Partial<Organization>) => {
      if (!isAuthenticated || !user) {
        throw new Error('Authentication required');
      }

      if (currentOrganization && currentOrganization.id !== organizationId) {
        throw new Error('Organization context mismatch');
      }

      setLoading(true);
      setError(null);

      try {
        const payload = {
          name: updates.name,
          description: updates.description,
        };
        const updatedOrganization = await apiFetch('/api/v1/organizations/me', {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        const normalizedOrganization = normalizeOrganization(
          updatedOrganization,
          user.role,
          user.organization_id,
        );
        const userOrganization: UserOrganization = {
          organization: normalizedOrganization,
          role: user.role,
          is_owner: user.role === 'org_admin' || user.role === 'super_admin',
          joined_at: user.created_at ?? new Date().toISOString(),
        };

        setCurrentOrganization(normalizedOrganization);
        setUserOrganizations([userOrganization]);
      } catch (organizationError) {
        console.error('Failed to update organization:', organizationError);
        const nextError =
          organizationError instanceof Error
            ? organizationError.message
            : 'Failed to update organization';
        setError(nextError);
        throw organizationError;
      } finally {
        setLoading(false);
      }
    },
    [currentOrganization, isAuthenticated, user],
  );

  const currentUserOrg = useMemo(
    () => userOrganizations.find((userOrganization) => userOrganization.organization.id === currentOrganization?.id),
    [currentOrganization?.id, userOrganizations],
  );

  const currentRole = currentUserOrg?.role ?? user?.role;
  const isOrganizationOwner = Boolean(currentUserOrg?.is_owner);
  const isOrganizationAdmin = Boolean(currentRole && canEditOrganizationSettings(currentRole));
  const canManageUsers = Boolean(currentRole && roleCanManageUsers(currentRole));
  const canCreateContent = Boolean(
    currentRole &&
      ['super_admin', 'org_admin', 'campaign_manager', 'content_creator', 'partner_user', 'agency_operator'].includes(
        currentRole,
      ),
  );

  const value = useMemo<OrganizationContextType>(
    () => ({
      currentOrganization,
      userOrganizations,
      loading,
      error,
      switchOrganization,
      refreshOrganizations,
      createOrganization,
      updateOrganization,
      isOrganizationOwner,
      isOrganizationAdmin,
      canManageUsers,
      canCreateContent,
    }),
    [
      canCreateContent,
      canManageUsers,
      createOrganization,
      currentOrganization,
      error,
      isOrganizationAdmin,
      isOrganizationOwner,
      loading,
      refreshOrganizations,
      switchOrganization,
      updateOrganization,
      userOrganizations,
    ],
  );

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};

export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};
